import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobsApi, formatApiError } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEvent } from '../../contexts/RealtimeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import { 
    Plus, Search, Briefcase, Users, MapPin, Clock, 
    MoreVertical, Edit, Trash2, Pause, Play, Eye, ChevronLeft, ChevronRight,
    Brain, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { BatchQuotaDialog } from '../../components/BatchQuotaDialog';

export default function JobsListPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [ownerFilter, setOwnerFilter] = useState('all');
    const [deleteJob, setDeleteJob] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [permissions, setPermissions] = useState([]);
    const PAGE_SIZE = 20;
    const navigate = useNavigate();

    // Batch analysis state. `batchEsRef` holds the current EventSource so we
    // can guarantee it is closed on unmount, on dialog close, and before
    // opening a new one. Without this, the browser leaks HTTP/1.1
    // connections (each SSE eats one of the ~6 per-origin slots) and
    // subsequent page loads eventually time out with "Erreur de chargement".
    const [batchDialog, setBatchDialog] = useState(null);
    const [batchProgress, setBatchProgress] = useState(null);
    const batchEsRef = useRef(null);
    // Pre-launch quota check: { job, preview, notice } when quota is short.
    const [quotaDialog, setQuotaDialog] = useState(null);
    const [quotaBusy, setQuotaBusy] = useState(false);

    const closeBatchStream = useCallback(() => {
        if (batchEsRef.current) {
            try { batchEsRef.current.close(); } catch { /* noop */ }
            batchEsRef.current = null;
        }
    }, []);

    // Ensure the SSE stream is closed if the page unmounts (navigation,
    // logout, ...). The backend job keeps running independently — closing
    // the client-side EventSource only releases the browser connection.
    useEffect(() => {
        return () => {
            closeBatchStream();
        };
    }, [closeBatchStream]);

    useEffect(() => {
        loadJobs();
    }, [statusFilter, ownerFilter, page]);

    // Realtime: keep progress state in sync with server events published
    // during batch analysis, even if the local per-batch SSE dropped.
    useRealtimeEvent('batch.progress', (payload) => {
        setBatchProgress((prev) => {
            // Only apply when the same job's batch is currently opened.
            if (!batchDialog || payload.job_id !== batchDialog.id) return prev;
            return { ...prev, ...payload, status: 'processing' };
        });
    });

    // On batch.completed anywhere in the org, refresh the jobs list so the
    // "candidates analysed" counters reflect reality without a manual F5.
    useRealtimeEvent('batch.completed', (payload) => {
        loadJobs();
        setBatchProgress((prev) => {
            if (!batchDialog || payload.job_id !== batchDialog.id) return prev;
            return {
                ...prev,
                status: 'complete',
                progress: 100,
                total: payload.total,
                analyzed: payload.analyzed,
                failed: payload.failed,
                quota_skipped: payload.quota_skipped || 0,
                current: payload.total,
                message: payload.message,
            };
        });
    });

    // A candidate score change in the org may affect the analytics
    // counters on job cards (e.g. avg score, number analysed). We debounce
    // to at most one refresh per 2s so a 1500-CV batch does not trigger
    // 1500 refreshes — the final `batch.completed` also refreshes.
    const candidateRefreshTimer = useRef(null);
    useRealtimeEvent('candidate.updated', () => {
        if (candidateRefreshTimer.current) return;
        candidateRefreshTimer.current = setTimeout(() => {
            candidateRefreshTimer.current = null;
            loadJobs();
        }, 2000);
    });
    useEffect(() => () => {
        if (candidateRefreshTimer.current) clearTimeout(candidateRefreshTimer.current);
    }, []);

    const loadJobs = async () => {
        try {
            const status = statusFilter === 'all' ? undefined : statusFilter;
            const owner = ownerFilter === 'mine' ? 'mine' : undefined;
            const skip = (page - 1) * PAGE_SIZE;
            const response = await jobsApi.getAll(status, skip, PAGE_SIZE, owner);
            setJobs(response.data.jobs || []);
            setTotal(response.data.total || 0);
            setTotalPages(response.data.pages || 1);
            if (response.data.permissions) setPermissions(response.data.permissions);
        } catch (error) {
            toast.error(t('jobs.error_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (jobId, newStatus) => {
        try {
            await jobsApi.updateStatus(jobId, newStatus);
            toast.success(t('jobs.status_updated'));
            loadJobs();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const handleDelete = async () => {
        if (!deleteJob) return;
        try {
            await jobsApi.delete(deleteJob.id);
            toast.success(t('jobs.trash_success'));
            setDeleteJob(null);
            loadJobs();
        } catch (error) {
            toast.error(formatApiError(error));
        }
    };

    const fetchBatchPreview = async (job) => (await jobsApi.analyzeBatchPreview(job.id)).data;

    // Checks eligible CVs vs remaining credits BEFORE launching anything.
    const handleBatchAnalyze = async (job) => {
        let preview;
        try {
            preview = await fetchBatchPreview(job);
        } catch (error) {
            toast.error(formatApiError(error));
            return;
        }
        if (preview.total <= preview.remaining_credits) {
            startBatch(job, null);
            return;
        }
        setQuotaDialog({ job, preview, notice: null });
    };

    // Re-checks the quota at confirmation time; if it changed in a way that
    // invalidates the user's choice, refreshes the dialog instead of launching.
    const launchFromQuotaDialog = async (selectedIds) => {
        const { job, preview } = quotaDialog;
        const all = selectedIds === null;
        setQuotaBusy(true);
        try {
            const fresh = await fetchBatchPreview(job);
            const freshIds = new Set(fresh.candidates.map((c) => c.id));
            const requested = all ? preview.candidates.map((c) => c.id) : selectedIds;
            const wanted = requested.filter((id) => freshIds.has(id));
            const stillValid = all
                ? (fresh.remaining_credits === preview.remaining_credits && fresh.total === preview.total)
                    || wanted.length <= fresh.remaining_credits
                : wanted.length === requested.length && wanted.length <= fresh.remaining_credits;
            if (!stillValid || wanted.length === 0) {
                setQuotaDialog({ job, preview: fresh, notice: t('jobs.batch_quota_changed') });
                return;
            }
            setQuotaDialog(null);
            startBatch(job, wanted);
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setQuotaBusy(false);
        }
    };

    const startBatch = async (job, candidateIds) => {
        // Close any lingering stream from a previous batch before starting.
        closeBatchStream();

        setBatchDialog(job);
        setBatchProgress({ status: 'starting', progress: 0, total: 0, analyzed: 0, failed: 0 });

        try {
            const res = await jobsApi.analyzeBatch(job.id, candidateIds);
            const data = res.data;

            if (!data.task_id) {
                // No pending candidates: backend returns { task_id: null, total: 0, message }.
                setBatchProgress({ status: 'complete', progress: 100, total: 0, analyzed: 0, failed: 0, message: data.message });
                loadJobs();
                return;
            }

            // Seed the dialog with the REAL total straight from the POST
            // response so the user never sees "0 / 0" while the SSE is
            // establishing (fixes the "bloqué à 0%" flash).
            setBatchProgress({
                status: 'starting',
                progress: 0,
                total: data.total || 0,
                analyzed: 0,
                failed: 0,
                current: 0,
            });

            // Listen to SSE for progress. Store the EventSource in a ref
            // so cleanup is guaranteed (unmount, dialog close, retry, ...).
            const es = new EventSource(jobsApi.getAnalyzeProgressUrl(data.task_id), { withCredentials: true });
            batchEsRef.current = es;

            es.onmessage = (event) => {
                try {
                    const progress = JSON.parse(event.data);
                    setBatchProgress(progress);

                    if (progress.status === 'complete') {
                        closeBatchStream();
                        loadJobs();
                    }
                } catch { /* ignore malformed frames */ }
            };

            es.onerror = () => {
                // The stream can drop for two reasons: (a) the backend
                // finished and closed the response (which is normal — we
                // will have already received `status: complete` above and
                // closed the ref), or (b) a transient network issue. In
                // both cases the SAFE thing is to release the browser
                // connection immediately. The backend job itself keeps
                // running to completion — reopening the job page will show
                // the final result. We DO NOT fake a `status: complete`
                // here so a mid-run drop does not lie to the user.
                closeBatchStream();
                setBatchProgress((prev) => {
                    if (!prev || prev.status === 'complete') return prev;
                    return {
                        ...prev,
                        status: 'complete',
                        message: t('jobs.batch_complete'),
                    };
                });
                loadJobs();
            };
        } catch (error) {
            closeBatchStream();
            setBatchProgress({ status: 'complete', progress: 0, total: 0, analyzed: 0, failed: 0, message: formatApiError(error) });
        }
    };

    const filteredJobs = jobs.filter(job => 
        job.title.toLowerCase().includes(search.toLowerCase()) ||
        job.location?.toLowerCase().includes(search.toLowerCase())
    );

    const getStatusBadge = (status) => {
        switch (status) {
            case 'active':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#D1FAE5] text-[#059669]">Active</span>;
            case 'paused':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#D97706]">En pause</span>;
            case 'closed':
                return <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#6B7280]">Fermée</span>;
            default:
                return null;
        }
    };

    const canViewAll = permissions.includes('view_all_jobs');
    const isAdmin = user?.role === 'admin';

    return (
        <div className="space-y-6" data-testid="jobs-list-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] flex items-center gap-2">
                        {t('jobs.page_title')}
                        {isAdmin && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-[#002FA7] text-white uppercase tracking-wide" data-testid="admin-badge">Admin</span>
                        )}
                    </h1>
                    <p className="text-[#4B5563]">
                        {isAdmin ? t('jobs.admin_subtitle') : t('jobs.user_subtitle')}
                    </p>
                </div>
                <Link to="/dashboard/jobs/new">
                    <Button className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="create-job-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('jobs.new_job')}
                    </Button>
                </Link>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                    <Input
                        placeholder={t('jobs.search_placeholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="job-search-input"
                    />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="status-filter">
                        <SelectValue placeholder={t('jobs.all_statuses')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">{t('jobs.all_statuses')}</SelectItem>
                        <SelectItem value="active">{t('jobs.status_active')}</SelectItem>
                        <SelectItem value="paused">{t('jobs.status_paused')}</SelectItem>
                        <SelectItem value="closed">{t('jobs.status_closed')}</SelectItem>
                    </SelectContent>
                </Select>
                {canViewAll && (
                    <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                        <SelectTrigger className="w-[180px]" data-testid="owner-filter">
                            <SelectValue placeholder={t('jobs.all_jobs')} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('jobs.all_jobs')}</SelectItem>
                            <SelectItem value="mine">{t('jobs.my_jobs')}</SelectItem>
                        </SelectContent>
                    </Select>
                )}
            </div>

            {/* Jobs list */}
            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-32 bg-white rounded-lg border border-[#E5E7EB] skeleton" />
                    ))}
                </div>
            ) : filteredJobs.length > 0 ? (
                <div className="space-y-4">
                    {filteredJobs.map((job) => (
                        <div 
                            key={job.id}
                            className="bg-white p-6 rounded-lg border border-[#E5E7EB] hover:border-[#002FA7]/30 hover:shadow-sm transition-all"
                            data-testid={`job-card-${job.id}`}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                                            {job.title}
                                        </h3>
                                        {getStatusBadge(job.status)}
                                        {job.pending_count > 0 && (
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#FEF3C7] text-[#D97706]" data-testid={`pending-badge-${job.id}`}>
                                                {t('jobs.pending_count', { count: job.pending_count })}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[#4B5563] mt-2 line-clamp-2">{job.description}</p>
                                    {isAdmin && job.organization_name && (
                                        <p className="text-xs text-[#9CA3AF] mt-1 flex items-center gap-1" data-testid={`org-label-${job.id}`}>
                                            {t('jobs.organization')}: <span className="font-medium text-[#4B5563]">{job.organization_name}</span>
                                        </p>
                                    )}
                                    
                                    <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#4B5563]">
                                        {job.location && (
                                            <span className="flex items-center gap-1">
                                                <MapPin className="w-4 h-4" />
                                                {job.location}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {job.employment_type === 'full-time' ? t('jobs.employment_full_time') :
                                             job.employment_type === 'part-time' ? t('jobs.employment_part_time') :
                                             job.employment_type === 'contract' ? t('jobs.employment_contract') : t('jobs.employment_remote')}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {(job.candidates_count || 0) <= 1
                                                ? t('jobs.candidate_one', { count: job.candidates_count || 0 })
                                                : t('jobs.candidate_other', { count: job.candidates_count })}
                                        </span>
                                    </div>

                                    {job.skills_required?.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-4">
                                            {job.skills_required.slice(0, 5).map((skill, index) => (
                                                <span 
                                                    key={index}
                                                    className="px-2 py-1 bg-[#F3F4F6] text-[#4B5563] text-xs rounded"
                                                >
                                                    {skill.name}
                                                </span>
                                            ))}
                                            {job.skills_required.length > 5 && (
                                                <span className="text-xs text-[#9CA3AF]">
                                                    {t('jobs.more_skills', { count: job.skills_required.length - 5 })}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" data-testid={`job-menu-${job.id}`}>
                                            <MoreVertical className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => navigate(`/dashboard/jobs/${job.id}`)}>
                                            <Eye className="w-4 h-4 mr-2" />
                                            Voir détails
                                        </DropdownMenuItem>
                                        {job.can_edit && (
                                            <DropdownMenuItem onClick={() => navigate(`/dashboard/jobs/${job.id}/edit`)}>
                                                <Edit className="w-4 h-4 mr-2" />
                                                Modifier
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem 
                                            onClick={() => job.pending_count > 0 && handleBatchAnalyze(job)}
                                            disabled={!job.pending_count || job.pending_count === 0}
                                            className={!job.pending_count ? 'opacity-50' : ''}
                                            data-testid={`batch-analyze-${job.id}`}
                                        >
                                            <Brain className="w-4 h-4 mr-2" />
                                            Analyser CVs ({job.pending_count || 0})
                                        </DropdownMenuItem>
                                        {job.can_edit && (
                                            <>
                                                {job.status === 'active' ? (
                                                    <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'paused')}>
                                                        <Pause className="w-4 h-4 mr-2" />
                                                        Mettre en pause
                                                    </DropdownMenuItem>
                                                ) : job.status === 'paused' ? (
                                                    <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'active')}>
                                                        <Play className="w-4 h-4 mr-2" />
                                                        Réactiver
                                                    </DropdownMenuItem>
                                                ) : null}
                                                <DropdownMenuItem 
                                                    onClick={() => setDeleteJob(job)}
                                                    className="text-[#EF4444] focus:text-[#EF4444]"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Supprimer
                                                </DropdownMenuItem>
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
                    <Briefcase className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" />
                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        Aucune offre trouvée
                    </h3>
                    <p className="text-[#4B5563] mt-2">
                        {search ? 'Aucune offre ne correspond à votre recherche' : 'Créez votre première offre d\'emploi'}
                    </p>
                    {!search && (
                        <Link to="/dashboard/jobs/new">
                            <Button className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white">
                                <Plus className="w-4 h-4 mr-2" />
                                Créer une offre
                            </Button>
                        </Link>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between px-2 py-3" data-testid="jobs-pagination">
                    <p className="text-sm text-[#9CA3AF]">{total} offre{total !== 1 ? 's' : ''}</p>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1} data-testid="jobs-prev-page">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-[#4B5563] min-w-[80px] text-center">{page} / {totalPages}</span>
                        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} data-testid="jobs-next-page">
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Batch Analysis Progress Dialog */}
            <Dialog
                open={!!batchDialog}
                onOpenChange={(open) => {
                    if (!open) {
                        // Closing the dialog releases the SSE stream on the
                        // client (frees a browser connection) — the backend
                        // batch job keeps running independently. Reopening
                        // the job page will show the final analysed count.
                        closeBatchStream();
                        if (batchProgress?.status === 'complete') {
                            setBatchDialog(null);
                            setBatchProgress(null);
                        } else {
                            setBatchDialog(null);
                        }
                    }
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {batchProgress?.status === 'complete' ? (
                                <CheckCircle className="w-5 h-5 text-[#059669]" />
                            ) : (
                                <Brain className="w-5 h-5 text-[#002FA7]" />
                            )}
                            {t('jobs.batch_analyze_title')} — {batchDialog?.title}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2" data-testid="batch-progress-dialog">
                        {batchProgress?.status !== 'complete' ? (
                            <>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-[#4B5563]">{t('jobs.batch_analyzing')}</span>
                                    <span className="font-medium text-[#002FA7]">{batchProgress?.progress || 0}%</span>
                                </div>
                                <Progress value={batchProgress?.progress || 0} className="h-3" />
                                <p className="text-sm text-[#9CA3AF] text-center">
                                    {t('jobs.batch_progress', { current: batchProgress?.current || 0, total: batchProgress?.total || 0 })}
                                </p>
                            </>
                        ) : (
                            <div className="text-center space-y-3">
                                {batchProgress.analyzed > 0 ? (
                                    <div className="flex items-center justify-center gap-2 text-[#059669]">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="font-medium">{t('jobs.batch_success', { count: batchProgress.analyzed })}</span>
                                    </div>
                                ) : null}
                                {batchProgress.failed > 0 && (
                                    <div className="flex items-center justify-center gap-2 text-[#EF4444]">
                                        <AlertCircle className="w-5 h-5" />
                                        <span>{t('jobs.batch_failed', { count: batchProgress.failed })}</span>
                                    </div>
                                )}
                                {batchProgress.quota_skipped > 0 && (
                                    <div className="flex items-center justify-center gap-2 text-[#B45309]" data-testid="batch-quota-skipped">
                                        <AlertCircle className="w-5 h-5" />
                                        <span>{t('jobs.batch_quota_skipped', { count: batchProgress.quota_skipped })}</span>
                                    </div>
                                )}
                                {batchProgress.total === 0 && (
                                    <p className="text-[#9CA3AF]">{t('jobs.batch_no_pending')}</p>
                                )}
                                <Button onClick={() => { setBatchDialog(null); setBatchProgress(null); }} className="mt-4 bg-[#002FA7] hover:bg-[#002480] text-white">
                                    {t('common.close')}
                                </Button>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <BatchQuotaDialog
                open={!!quotaDialog}
                job={quotaDialog?.job}
                preview={quotaDialog?.preview}
                notice={quotaDialog?.notice}
                busy={quotaBusy}
                onClose={() => setQuotaDialog(null)}
                onLaunchSelected={(ids) => launchFromQuotaDialog(ids)}
                onLaunchAll={() => launchFromQuotaDialog(null)}
            />

            {/* Delete confirmation */}
            <AlertDialog open={!!deleteJob} onOpenChange={() => setDeleteJob(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('jobs.trash_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('jobs.trash_description_part1', { title: deleteJob?.title })}{' '}
                            <strong>{t('jobs.trash_description_days')}</strong>{' '}{t('jobs.trash_description_part2')}
                            <br /><br />
                            {t('jobs.trash_description_candidates')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
                        >
                            {t('common.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
